import React from 'react';
import { SelectOptionElement } from '@odin-labs/components';
import {
  useGetJobsiteCardFormatsQuery,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import { to } from 'acl';
import { ensureNonEmptyItems } from 'utils';
import { BriefcaseIcon } from 'components/icons';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { BadgeType } from 'containers/worker/modals/badgeModal/components/types';
import { BadgeTypeOption, ChooseBadgeTypeData } from './types';

const getBadgeTypeOptions = (
  qrCodeCardFormatId: string,
  mobileBadgeCardFormatId: string,
  facialRecognitionFormatId: string,
  areBadgesAvailable: boolean,
  arePvcBadgesAvailable: boolean,
  canManageTempBadges: boolean,
): BadgeTypeOption[] =>
  ensureNonEmptyItems<BadgeTypeOption>([
    areBadgesAvailable && {
      value: BadgeType.NewBadge,
      label: 'Print and register a permanent badge',
      // disabled: !areBadgesAvailable,
      description:
        'Print a proximity card that can be used for tap and scan-based entries.',
      disabledDescription: "This jobsite doesn't support badge assignment",
    },
    areBadgesAvailable && {
      value: BadgeType.ExistingBadge,
      label: 'Register a permanent badge',
      description:
        'Register a badge that has already been printed for the worker.',
      // disabled: !areBadgesAvailable,
      disabledDescription: "This jobsite doesn't support badge assignment",
    },
    areBadgesAvailable &&
      canManageTempBadges && {
        value: BadgeType.TempBadge,
        label: 'Assign a temporary badge',
        description: 'Assign a temporary badge to this worker.',
        // disabled: !areBadgesAvailable,
        disabledDescription: "This jobsite doesn't support badge assignment",
      },
    arePvcBadgesAvailable && {
      value: BadgeType.NewIDCard,
      label: 'Print an ID card',
      // disabled: !areBadgesAvailable,
      description:
        'Print an ID card that can be used for scan-based entry only.',
      disabledDescription: "This jobsite doesn't support ID card assignment",
    },
    !!qrCodeCardFormatId && {
      value: BadgeType.QrCode,
      label: 'Assign a QR code sticker',
      description: 'Assign a QR code sticker to this worker.',
      // disabled: !qrCodeCardFormatId,
      disabledDescription: "This jobsite doesn't support this badge type",
      workerCardFormatId: qrCodeCardFormatId,
    },
    !!mobileBadgeCardFormatId && {
      value: BadgeType.MobileBadge,
      label: 'Issue mobile badge',
      description: 'Issue or reissue a mobile badge to this worker.',
      disabledDescription:
        'This worker already has an active mobile badge, please re-issue it',
      workerCardFormatId: mobileBadgeCardFormatId,
    },
    !!facialRecognitionFormatId && {
      value: BadgeType.FacialRecognition,
      label: 'Start facial recognition enrollment',
      description: 'Enroll this worker for facial recognition access.',
      disabledDescription:
        'This worker already has an active facial recognition badge, please re-issue it',
      workerCardFormatId: facialRecognitionFormatId,
    },
  ]);

const getFormInputs = ({
  jobsitesOptions,
  badgeTypeOptions,
}: {
  jobsitesOptions: SelectOptionElement[];
  badgeTypeOptions: BadgeTypeOption[];
}): TypedFormInputs<ChooseBadgeTypeData> => {
  return {
    jobsiteId: {
      element: FormInputTypes.Select,
      label: 'Jobsite',
      elementProps: {
        placeholder: 'Select one',
        icon: BriefcaseIcon,
        disabled: (jobsitesOptions?.length ?? 0) < 2,
        options: jobsitesOptions ?? [],
      },
      validation: { required: true },
      layout: GridColSpan.SpanFull,
    },
    badgeType: {
      element: FormInputTypes.RadioGroup,
      label: badgeTypeOptions?.length
        ? 'What kind of badge would you like to add?'
        : null,
      elementProps: {
        options: badgeTypeOptions ?? [],
        withAnimation: true,
      },
      validation: { required: true },
      layout: [
        GridColSpan.SpanFull,
        !badgeTypeOptions?.length && 'odin-hidden',
      ],
    },
  };
};

type BadgeTypeOptionsData = {
  badgeTypeOptions: BadgeTypeOption[];
  loading: boolean;
};

export const useBadgeTypeOptions = (
  jobsiteId: string,
): BadgeTypeOptionsData => {
  const { data: jobsiteCardFormatsData, loading } =
    useGetJobsiteCardFormatsQuery({
      fetchPolicy: 'no-cache',
      variables: { jobsiteId },
      skip: !jobsiteId,
    });
  const { currentUser: user } = React.useContext(AuthContext);
  const canManageTempBadges = user.isAllowed(to.manageTempBadges, jobsiteId);

  return React.useMemo((): BadgeTypeOptionsData => {
    const jobsiteCardFormats =
      jobsiteCardFormatsData?.getJobsite?.jobsiteCardFormats.edges.map(
        ({ node }) => node,
      );
    const qrCodeCardFormatId = jobsiteCardFormats?.find(
      (jcf) => jcf.workerCardFormat.cardType === WorkerCardType.QrCode,
    )?.workerCardFormat.workerCardFormatId;

    const areBadgesAvailable = jobsiteCardFormats?.some(
      (jcf) => jcf.workerCardFormat.cardType === WorkerCardType.Proximity,
    );

    const arePvcBadgesAvailable = jobsiteCardFormats?.some(
      (jcf) => jcf.workerCardFormat.cardType === WorkerCardType.Pvc,
    );

    const mobileBadgeFormatId = jobsiteCardFormats?.find(
      (jcf) => jcf.workerCardFormat.cardType === WorkerCardType.MobileBadge,
    )?.workerCardFormat.workerCardFormatId;

    const facialRecognitionFormatId = jobsiteCardFormats?.find(
      (jcf) =>
        jcf.workerCardFormat.cardType === WorkerCardType.FacialRecognition,
    )?.workerCardFormat.workerCardFormatId;

    return {
      badgeTypeOptions: getBadgeTypeOptions(
        qrCodeCardFormatId,
        mobileBadgeFormatId,
        facialRecognitionFormatId,
        areBadgesAvailable,
        arePvcBadgesAvailable,
        canManageTempBadges,
      ),
      loading,
    };
  }, [jobsiteCardFormatsData, canManageTempBadges, loading]);
};

export const getFormInputsHook =
  ({
    jobsitesOptions,
    setCanContinue,
  }: {
    jobsitesOptions: SelectOptionElement[];
    setCanContinue: (value: boolean) => void;
  }): UseInputs<ChooseBadgeTypeData> =>
  ({
    watch,
    setValue,
    getValues,
  }: UseFormMethods<ChooseBadgeTypeData>): TypedFormInputs<ChooseBadgeTypeData> => {
    const jobsiteIdElement = watch('jobsiteId');
    const jobsiteId = jobsiteIdElement?.value;

    const { badgeTypeOptions } = useBadgeTypeOptions(jobsiteId);

    const canContinue = !!badgeTypeOptions?.length;
    React.useEffect(() => {
      setCanContinue(canContinue);
    }, [canContinue]);

    React.useEffect(() => {
      const selectedOption = getValues().badgeType;
      const firstSelectableOption = badgeTypeOptions.find(
        (bto) => !bto.disabled,
      );
      if (selectedOption?.value !== firstSelectableOption?.value) {
        setValue('badgeType', firstSelectableOption ?? null, {
          shouldValidate: true,
        });
      }
    }, [badgeTypeOptions]);

    return React.useMemo(
      () =>
        getFormInputs({
          jobsitesOptions,
          badgeTypeOptions,
        }),
      [jobsitesOptions, badgeTypeOptions],
    );
  };

export const getFormDefaultValues = ({
  jobsitesOptions,
}: {
  jobsitesOptions: SelectOptionElement[];
}): ChooseBadgeTypeData => {
  return {
    jobsiteId: jobsitesOptions?.length === 1 ? jobsitesOptions[0] : null,
    badgeType: null,
  };
};
